const API_BASE = process.env.REACT_APP_API_BASE || 'https://sequentus-content.azurewebsites.net/api';
const CORE_API_BASE = process.env.REACT_APP_CORE_API_BASE || 'https://sequentus-core.azurewebsites.net/api';
const CORE_API_KEY = process.env.REACT_APP_CORE_API_KEY || '';

async function fetchApi(path: string, params: Record<string, string> = {}) {
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([k, v]) => { if (v) url.searchParams.set(k, v); });
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
}

async function postApi(path: string, body: any = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
}

// Dashboard APIs
export const getSanctionStats = (reg?: string, from?: string, to?: string) =>
  fetchApi('/si/sanction-stats', { regulator: reg || '', from: from || '', to: to || '' });

export const getTrends = (reg?: string, granularity?: string) =>
  fetchApi('/si/trends', { regulator: reg || '', granularity: granularity || 'month' });

export const getBenchmarking = (from?: string, to?: string) =>
  fetchApi('/si/benchmarking', { from: from || '', to: to || '' });

export const getRecent = (limit?: number, reg?: string) =>
  fetchApi('/si/recent', { limit: String(limit || 20), regulator: reg || '' });

export const getInsights = () => fetchApi('/si/insights');

export const getOutcome = (id: string) => fetchApi(`/si/outcome/${id}`);

export const getRecentJudgments = (limit?: number, reg?: string) =>
  fetchApi('/si/recent-judgments', { limit: String(limit || 20), regulator: reg || '' });

// Feed API
export const getFeed = (limit?: number, type?: string) =>
  fetchApi('/si/feed', { limit: String(limit || 20), type: type || '' });

// Editorial queue APIs
export const getEditorialQueue = (channel: string, status?: string) =>
  fetchApi(`/si/editorial/${channel}`, { status: status || 'pending' });

export const generateContent = (id: string) =>
  postApi(`/si/editorial/${id}/generate`);

export const approveItem = (id: string, approvedBy: string, editedContent?: any) =>
  postApi(`/si/editorial/${id}/approve`, { approvedBy, editedContent });

export const rejectItem = (id: string) =>
  postApi(`/si/editorial/${id}/reject`);

// Source management APIs
export const getSources = () => fetchApi('/si/sources');

export const createSource = (data: any) => postApi('/si/sources', data);

export const updateSource = (id: string, data: any) =>
  fetch(`${API_BASE}/si/sources/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(r => r.json());

export const deleteSource = (id: string) =>
  fetch(`${API_BASE}/si/sources/${id}`, { method: 'DELETE' }).then(r => r.json());

export const checkSource = (id: string) =>
  postApi(`/si/sources/${id}/check`);

// Prompt Library
export const getPrompts = () => fetchApi('/si/prompts');
export const getPrompt = (id: string) => fetchApi(`/si/prompts/${id}`);
export const createPrompt = (data: any) => postApi('/si/prompts', data);
export const updatePrompt = (id: string, data: any) =>
  fetch(`${API_BASE}/si/prompts/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(r => r.json());

// Cost Dashboard
export const getCosts = (days?: number) => fetchApi('/si/costs', { days: String(days || 30) });

// Representation comparison
export const getRepresentationComparison = (reg?: string) =>
  fetchApi('/si/representation-comparison', { regulator: reg || '' });

// Draft scenarios
export const getDraftScenarios = (status?: string) =>
  fetchApi('/si/draft-scenarios', { status: status || 'draft' });

export const promoteScenario = (id: string, edits?: any) =>
  postApi(`/si/draft-scenarios/${id}/promote`, edits || {});

export const rejectScenario = (id: string) =>
  postApi(`/si/draft-scenarios/${id}/reject`);

// LinkedIn
export const getLinkedInStatus = () => fetchApi('/si/linkedin/status');
export const postToLinkedIn = (channel: string, text: string) =>
  postApi('/si/linkedin/post', { channel, text });

// Analytics Dashboard (sequentus-core)
async function fetchCoreApi(path: string, params: Record<string, string> = {}) {
  const url = new URL(`${CORE_API_BASE}${path}`);
  Object.entries(params).forEach(([k, v]) => { if (v) url.searchParams.set(k, v); });
  if (CORE_API_KEY) url.searchParams.set('code', CORE_API_KEY);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Core API ${res.status}`);
  return res.json();
}

export const getAnalyticsLive = () => fetchCoreApi('/analytics-live');
export const getAnalyticsSnapshots = (range?: number) =>
  fetchCoreApi('/analytics-dashboard', { range: String(range || 12) });
export const getAnalyticsSnapshot = (week: number, year: number) =>
  fetchCoreApi('/analytics-dashboard', { week: String(week), year: String(year) });
